import { Fragment, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Circle,
  CornerDownLeft,
  History,
  LayoutGrid,
  Loader2,
  Sparkles,
  SquarePen,
  Sun,
  X,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  askAssistant,
  getBriefing,
  getConversation,
  type AnswerData,
  type AssistantMessage,
  type Proposal,
} from '@/lib/assistant';
import { applyProposal, type ApplyResult } from './apply-proposal';
import { CreateEmployeeFields, type EmployeeDraft } from './create-employee-fields';
import { ConversationHistory, conversationKeys } from './conversation-history';
import { CommandPalette } from './command-palette';
import { RichText } from './rich-text';
import { AnswerCards, hasRenderedData } from './answer-cards';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  proposals?: Proposal[];
  toolsUsed?: string[];
  /** Tool results, drawn as cards instead of being retold in the text. */
  data?: AnswerData[];
  /** Replayed from a stored conversation rather than produced this turn. */
  historical?: boolean;
}

const SUGGESTIONS = [
  'Who is away next week?',
  'Show me overdue tasks',
  'How many vacation days does Denys have left?',
  'What projects are active?',
];

/** Human label for a tool name, so the trace reads as English not as identifiers. */
const TOOL_LABEL: Record<string, string> = {
  search_people: 'people',
  get_person: 'employee profile',
  who_is_away: 'absences',
  get_absence_balance: 'absence balances',
  list_projects: 'projects',
  search_tasks: 'tasks',
  get_task_details: 'task details',
  search_comments: 'comments',
  get_sprint_summary: 'sprint',
  list_boards: 'boards',
  get_my_activity: 'my activity',
  list_positions: 'positions',
  list_departments: 'departments',
  get_headcount: 'headcount',
  get_team: 'team structure',
  get_joiners: 'new joiners',
  list_vacancies: 'open roles',
  get_compensation: 'compensation',
};

type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

/** Which area of the product a step touches — shown so a multi-module plan reads
 *  as one at a glance. */
const KIND_DOMAIN: Record<string, string> = {
  create_task: 'boards',
  assign_task: 'boards',
  set_due_date: 'boards',
  add_comment: 'boards',
  add_project_member: 'projects',
  create_employee: 'people',
};

function StepIcon({ state }: { state: StepState }) {
  if (state === 'running') return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-hygge" />;
  if (state === 'done') return <CheckCircle2 className="h-4 w-4 shrink-0 text-hygge" />;
  if (state === 'failed') return <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />;
  if (state === 'skipped') return <Circle className="h-4 w-4 shrink-0 text-muted-foreground/50" />;
  return <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

/**
 * A plan: every change the assistant prepared in this turn, applied on one click
 * and reported step by step.
 *
 * Steps run in sequence, not in parallel — a later step often depends on an
 * earlier one (adding someone to the project before handing them its tasks), and
 * a serial run makes a failure legible: everything before it happened, everything
 * after it did not. On failure we stop rather than press on, so the user is never
 * left guessing which half of a plan landed.
 */
export function PlanCard({ proposals, historical }: { proposals: Proposal[]; historical?: boolean }) {
  const qc = useQueryClient();
  const [states, setStates] = useState<StepState[]>(() => proposals.map(() => 'pending'));
  const [errors, setErrors] = useState<(string | null)[]>(() => proposals.map(() => null));
  const [links, setLinks] = useState<(ApplyResult['link'] | null)[]>(() => proposals.map(() => null));
  const [running, setRunning] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // What the user changed on an editable card. Kept apart from the proposal so
  // the assistant's original suggestion stays visible in the transcript.
  const [edits, setEdits] = useState<Record<string, unknown>[]>(() => proposals.map(() => ({})));

  if (dismissed) return null;

  const doneCount = states.filter((s) => s === 'done').length;
  const finished = running === false && doneCount > 0;
  const isPlan = proposals.length > 1;

  /** Applies one step on its own — how a deliberate repeat gets through. */
  const applyOne = async (i: number) => {
    setRunning(true);
    const next = [...states];
    next[i] = 'running';
    setStates([...next]);
    try {
      const outcome = await applyProposal(
        { ...proposals[i], payload: { ...proposals[i].payload, ...edits[i] } },
        qc,
      );
      next[i] = 'done';
      if (outcome.link) setLinks((prev) => prev.map((l, j) => (j === i ? outcome.link! : l)));
      toast.success('Applied');
    } catch (err) {
      next[i] = 'failed';
      const message = err instanceof Error ? err.message : 'Failed';
      setErrors((prev) => prev.map((e, j) => (j === i ? message : e)));
      toast.error(message);
    }
    setStates([...next]);
    setRunning(false);
  };

  const applyAll = async () => {
    setRunning(true);
    const next = [...states];
    const nextErrors = [...errors];
    const nextLinks = [...links];

    for (let i = 0; i < proposals.length; i++) {
      if (next[i] === 'done') continue;
      // A change already put to the user earlier is not part of "apply all". The
      // assistant re-lists what it has already prepared when asked to add one
      // more, and approving that in one press logs the same hours twice. It can
      // still be applied deliberately, from its own button.
      if (proposals[i].repeat) continue;
      next[i] = 'running';
      setStates([...next]);
      try {
        // Apply what is on screen, not what was first suggested — the card is
        // editable precisely so the human can correct the model before it lands.
        const outcome = await applyProposal(
          { ...proposals[i], payload: { ...proposals[i].payload, ...edits[i] } },
          qc,
        );
        next[i] = 'done';
        if (outcome.link) {
          nextLinks[i] = outcome.link;
          setLinks([...nextLinks]);
        }
      } catch (err) {
        next[i] = 'failed';
        nextErrors[i] = err instanceof Error ? err.message : 'Failed';
        // Stop here: the remaining steps may depend on this one, and applying
        // them anyway would leave a half-done plan nobody asked for.
        for (let j = i + 1; j < proposals.length; j++) {
          if (next[j] === 'pending') next[j] = 'skipped';
        }
        setStates([...next]);
        setErrors([...nextErrors]);
        setRunning(false);
        toast.error(`Stopped at step ${i + 1}: ${nextErrors[i]}`);
        return;
      }
      setStates([...next]);
    }

    setRunning(false);
    toast.success(isPlan ? `Applied ${proposals.length} changes` : 'Applied');
  };

  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-border bg-muted/20">
      {isPlan && (
        <p className="border-b border-border px-3 py-2 text-[11px] font-medium text-muted-foreground">
          Plan · {proposals.length} changes
        </p>
      )}

      <ul>
        {proposals.map((p, i) => (
          <li
            key={i}
            className={cn(
              'flex items-start gap-2.5 px-3 py-2.5',
              i < proposals.length - 1 && 'border-b border-border/60',
            )}
          >
            <StepIcon state={states[i]} />
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-relaxed text-foreground">{p.summary}</p>
              {/* Creating a person is the one proposal worth editing in place: the
                  model reads the sentence well but cannot know this company calls
                  the job "Node.js Dev". The same component renders before and
                  after applying — read-only afterwards — so the card can never
                  report a different value than the one that was written. */}
              {p.kind === 'create_employee' ? (
                <CreateEmployeeFields
                  draft={{ ...(p.payload as EmployeeDraft), ...(edits[i] as EmployeeDraft) }}
                  disabled={running}
                  readOnly={states[i] === 'done' || historical}
                  onChange={(patch) =>
                    setEdits((prev) => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)))
                  }
                />
              ) : (
                p.details &&
                p.details.length > 0 && (
                  <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                    {p.details.map((d) => (
                      <Fragment key={d.label}>
                        <dt className="text-[11px] text-muted-foreground">{d.label}</dt>
                        <dd className="min-w-0 break-words text-[11px] font-medium text-foreground">
                          {d.value}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                )
              )}
              {/* Said plainly, next to the change it is about. The reader is one
                  press away from logging the same hours twice, and the only
                  person who knows whether that is intended is them. */}
              {p.repeat && states[i] === 'pending' && !historical && (
                <div className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5">
                  <p className="text-[11px] text-muted-foreground">
                    Already proposed earlier in this conversation — left out of Apply all so it is
                    not done twice.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={running}
                    className="mt-1.5 h-7 text-xs"
                    onClick={() => void applyOne(i)}
                  >
                    Do it again anyway
                  </Button>
                </div>
              )}
              {errors[i] && <p className="mt-1 text-[11px] text-destructive">{errors[i]}</p>}
              {/* Reaching the new record is the point of the whole exchange, so it
                  gets a button. As a thin text link it read as a caption and was
                  missed. */}
              {links[i] && (
                <Button asChild size="sm" variant="outline" className="mt-2 h-7 text-xs">
                  <Link to={links[i]!.href} search={links[i]!.search}>
                    {links[i]!.label}
                    <ArrowUpRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>
              )}
            </div>
            <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/70">
              {KIND_DOMAIN[p.kind] ?? ''}
            </span>
          </li>
        ))}
      </ul>

      {/* A replayed conversation is a record, not a control panel. The card cannot
          tell whether these changes were applied at the time, and offering Approve
          on a stale proposal invites creating the same person twice. */}
      {historical ? (
        <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          From an earlier conversation — ask again to make this change now.
        </p>
      ) : (
      <div className="flex items-center gap-2 border-t border-border px-3 py-2.5">
        {doneCount === proposals.length ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-hygge">
            <Check className="h-3.5 w-3.5" />
            {isPlan ? `All ${proposals.length} applied` : 'Applied'}
          </p>
        ) : (
          <>
            <Button size="sm" className="h-7 text-xs" onClick={applyAll} disabled={running}>
              {running
                ? 'Applying…'
                : finished
                  ? 'Retry remaining'
                  : isPlan
                    ? 'Apply all'
                    : 'Approve'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setDismissed(true)}
              disabled={running}
            >
              {isPlan ? 'Dismiss' : 'Reject'}
            </Button>
            {doneCount > 0 && (
              <span className="ml-auto text-[11px] text-muted-foreground">
                {doneCount} of {proposals.length} done
              </span>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
}

export function AssistantPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // The thread these turns belong to on the server. Undefined until the first
  // reply comes back, which is when the conversation actually starts existing.
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [showHistory, setShowHistory] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const qc = useQueryClient();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, busy]);

  const startNew = () => {
    setTurns([]);
    setConversationId(undefined);
    setShowHistory(false);
  };

  /** Reopens a past conversation, proposals and tool trace included. */
  const openConversation = async (id: string) => {
    setLoadingConversation(true);
    try {
      const conversation = await getConversation(id);
      setTurns(
        conversation.messages.map((m) => ({
          role: m.role,
          content: m.content,
          proposals: m.proposals ?? undefined,
          toolsUsed: m.toolsUsed,
          historical: true,
        })),
      );
      setConversationId(conversation.id);
      setShowHistory(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open that conversation');
    } finally {
      setLoadingConversation(false);
    }
  };

  /** Briefing: the assistant looks on its own instead of answering a question. The
   *  prompt lives on the server, so this button and a future scheduled run stay
   *  identical. */
  const runBriefing = async () => {
    if (busy) return;
    setTurns((prev) => [...prev, { role: 'user', content: 'What should I know today?' }]);
    setBusy(true);
    try {
      const reply = await getBriefing();
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: reply.text,
          proposals: reply.proposals,
          toolsUsed: reply.toolsUsed,
          data: reply.data,
        },
      ]);
    } catch (err) {
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: err instanceof Error ? `Sorry — the briefing failed: ${err.message}` : 'Sorry — the briefing failed.',
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || busy) return;

    const history: AssistantMessage[] = [
      ...turns.map((t) => ({ role: t.role, content: t.content })),
      { role: 'user' as const, content: question },
    ];

    setTurns((prev) => [...prev, { role: 'user', content: question }]);
    setInput('');
    setBusy(true);

    try {
      const reply = await askAssistant(history, undefined, conversationId);
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: reply.text,
          proposals: reply.proposals,
          toolsUsed: reply.toolsUsed,
          data: reply.data,
        },
      ]);
      if (reply.conversationId) {
        setConversationId(reply.conversationId);
        // The list orders by activity and shows a message count, so both move
        // with every turn — not only when a conversation is created.
        void qc.invalidateQueries({ queryKey: conversationKeys.all });
      }
    } catch (err) {
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            err instanceof Error
              ? `Sorry — that request failed: ${err.message}`
              : 'Sorry — that request failed.',
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Hide the Sheet's built-in top-right close button ([&>button.absolute]) —
        // the panel header already renders its own close X, and showing both put
        // two close affordances a few pixels apart.
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px] [&>button.absolute]:hidden"
      >
        <SheetTitle className="sr-only">HyggeOS assistant</SheetTitle>
        <SheetDescription className="sr-only">
          Ask questions across people, absences, projects and boards, and prepare changes to apply.
        </SheetDescription>

        <div className="flex items-center gap-1 border-b border-border px-4 py-3">
          <Sparkles className="h-4 w-4 text-hygge" />
          <span className="mr-auto text-sm font-medium">Assistant</span>

          <button
            type="button"
            onClick={() => {
              setShowCommands((v) => !v);
              setShowHistory(false);
            }}
            className={cn(
              'cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
              showCommands && 'bg-muted text-foreground',
            )}
            aria-label="Commands"
            aria-pressed={showCommands}
            title="What can this assistant do?"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setShowHistory((v) => !v);
              setShowCommands(false);
            }}
            className={cn(
              'cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground',
              showHistory && 'bg-muted text-foreground',
            )}
            aria-label="Conversation history"
            aria-pressed={showHistory}
            title="Conversation history"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={startNew}
            disabled={turns.length === 0}
            className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="New conversation"
            title="New conversation"
          >
            <SquarePen className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close assistant"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showCommands && (
          <div className="max-h-[55%] overflow-y-auto border-b border-border px-3 py-2">
            <CommandPalette
              busy={busy}
              onRun={(prompt) => {
                setShowCommands(false);
                void send(prompt);
              }}
            />
          </div>
        )}

        {showHistory && (
          <div className="max-h-[45%] overflow-y-auto border-b border-border px-3 py-2">
            <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Your conversations
            </p>
            <ConversationHistory activeId={conversationId} onOpen={(id) => void openConversation(id)} />
          </div>
        )}

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {loadingConversation && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {turns.length === 0 && (
            <div>
              <p className="text-sm text-muted-foreground">
                Ask about your people, absences, projects and boards — or tell me what to change.
              </p>

              {/* The one thing you didn't have to ask for: the assistant goes and
                  looks, instead of waiting to be queried. */}
              <button
                type="button"
                onClick={() => void runBriefing()}
                disabled={busy}
                className="mt-3 flex w-full cursor-pointer items-center gap-2 rounded-md border border-hygge/30 bg-hygge/5 px-3 py-2.5 text-left text-xs font-medium text-foreground hover:bg-hygge/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sun className="h-4 w-4 shrink-0 text-hygge" />
                What should I know today?
              </button>

              <button
                type="button"
                onClick={() => setShowCommands(true)}
                className="mt-2 flex w-full cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2.5 text-left text-xs font-medium text-foreground hover:bg-muted"
              >
                <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" />
                See everything I can do
              </button>

              <div className="mt-3 space-y-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    className="block w-full cursor-pointer rounded-md border border-border px-3 py-2 text-left text-xs text-foreground hover:bg-muted"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn, i) => (
            <div key={i} className={cn(turn.role === 'user' && 'flex justify-end')}>
              {turn.role === 'user' ? (
                <p className="max-w-[85%] rounded-lg bg-hygge/10 px-3 py-2 text-xs leading-relaxed text-foreground">
                  {turn.content}
                </p>
              ) : (
                <div>
                  <RichText text={turn.content} compact={hasRenderedData(turn.data)} />
                  <AnswerCards data={turn.data} />
                  {turn.proposals && turn.proposals.length > 0 && (
                    <PlanCard proposals={turn.proposals} historical={turn.historical} />
                  )}
                  {turn.toolsUsed && turn.toolsUsed.length > 0 && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Checked: {turn.toolsUsed.map((t) => TOOL_LABEL[t] ?? t).join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}

          {busy && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Working…
            </p>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3">
          <div className="relative">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              placeholder="Ask anything, or say what to change…"
              className="min-h-[64px] resize-none pr-10 text-xs"
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => void send(input)}
              disabled={busy || !input.trim()}
              className="absolute bottom-2 right-2 cursor-pointer rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send"
            >
              <CornerDownLeft className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Changes are never applied until you approve them.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
